import { useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/Button";

type RecoveryResult = {
  codigo: string;
  mensaje: string;
  pedido?: string;
  importe?: number;
  respuestaRedsys?: string;
  clienteId?: number;
  seguroId?: number;
  pilotoId?: number;
  pilotoIds?: number[];
  estadoActual?: string;
};

type Props = {
  endpoint: string;
  admin?: boolean;
};

function resultTone(codigo: string) {
  if (["validada", "procesado_correctamente", "ya_procesado"].includes(codigo)) {
    return "border-green-200 bg-green-50 text-green-800";
  }
  if (codigo === "pago_rechazado") return "border-amber-200 bg-amber-50 text-amber-900";
  return "border-red-200 bg-red-50 text-red-800";
}

export function RedsysRecoveryPanel({ endpoint, admin = false }: Props) {
  const [raw, setRaw] = useState("");
  const [result, setResult] = useState<RecoveryResult | null>(null);
  const [loading, setLoading] = useState<"check" | "process" | null>(null);

  const submit = async (soloComprobar: boolean) => {
    setLoading(soloComprobar ? "check" : "process");
    setResult(null);
    try {
      const response = await api.post<{ result: RecoveryResult }>(endpoint, {
        notificacion: raw.trim(),
        solo_comprobar: soloComprobar,
      });
      setResult(response.data.result);
    } catch (error: unknown) {
      const response = (error as {
        response?: { data?: { result?: RecoveryResult; error?: string } };
      }).response;
      setResult(
        response?.data?.result ?? {
          codigo: "error",
          mensaje: response?.data?.error ?? "No se pudo procesar la notificación.",
        }
      );
    } finally {
      setLoading(null);
    }
  };

  const canProcess = result?.codigo === "validada";

  return (
    <details className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <summary className="cursor-pointer select-none px-5 py-4 text-sm font-medium text-gray-700 hover:bg-gray-50">
        Herramientas de recuperación Redsys
      </summary>
      <div className="space-y-4 border-t border-gray-100 px-5 py-5">
        <div>
          <h3 className="font-medium text-gray-900">Reprocesar una notificación de pago</h3>
          <p className="mt-1 text-sm text-gray-600">
            {admin
              ? "Utiliza esta herramienta cuando Redsys haya notificado una operación, pero Circuit Cover no la haya reflejado."
              : "Úsala si Redsys ha confirmado un pago de tu TPV pero el piloto continúa pendiente."}
          </p>
        </div>
        <textarea
          value={raw}
          onChange={(event) => {
            setRaw(event.target.value);
            setResult(null);
          }}
          rows={6}
          spellCheck={false}
          autoComplete="off"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs text-gray-800 outline-none focus:border-[#1A3AB8] focus:ring-2 focus:ring-blue-100"
          placeholder="Ds_SignatureVersion=HMAC_SHA256_V1&Ds_MerchantParameters=...&Ds_Signature=..."
        />
        <p className="text-xs text-gray-500">
          Pega el cuerpo completo copiado de la notificación. Circuit Cover verificará la firma antes de realizar cualquier cambio.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={!raw.trim() || loading != null}
            loading={loading === "check"}
            onClick={() => void submit(true)}
          >
            Comprobar notificación
          </Button>
          {canProcess && (
            <Button
              type="button"
              disabled={loading != null}
              loading={loading === "process"}
              onClick={() => void submit(false)}
            >
              Procesar notificación
            </Button>
          )}
        </div>
        {result && (
          <div className={`rounded-lg border p-4 text-sm ${resultTone(result.codigo)}`}>
            <p className="font-medium">{result.mensaje}</p>
            {(result.pedido || result.importe != null || result.respuestaRedsys) && (
              <dl className="mt-3 grid gap-x-5 gap-y-1 text-xs sm:grid-cols-2">
                {result.pedido && <><dt className="opacity-70">Pedido</dt><dd className="font-mono">{result.pedido}</dd></>}
                {result.importe != null && <><dt className="opacity-70">Importe</dt><dd>{result.importe.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</dd></>}
                {result.respuestaRedsys && <><dt className="opacity-70">Respuesta Redsys</dt><dd>{result.respuestaRedsys}</dd></>}
                {result.estadoActual && <><dt className="opacity-70">Estado actual</dt><dd>{result.estadoActual}</dd></>}
                {admin && result.clienteId != null && <><dt className="opacity-70">Cliente</dt><dd>#{result.clienteId}</dd></>}
                {admin && result.seguroId != null && <><dt className="opacity-70">Seguro</dt><dd>#{result.seguroId}</dd></>}
                {result.pilotoId != null && <><dt className="opacity-70">Piloto</dt><dd>#{result.pilotoId}</dd></>}
                {result.pilotoId == null && result.pilotoIds && result.pilotoIds.length > 0 && (
                  <><dt className="opacity-70">Pilotos</dt><dd>{result.pilotoIds.map((id) => `#${id}`).join(", ")}</dd></>
                )}
              </dl>
            )}
          </div>
        )}
      </div>
    </details>
  );
}
